import { Modal, Descriptions, Spin, Typography, Button, Image, Divider, Space } from 'antd';
import { DownloadOutlined, PictureOutlined } from '@ant-design/icons';
import { useWisecureQuery, useWisecureClient } from '@wisecure/api-client/hooks';
import type { InterventionDetailResponse } from '@wisecure/api-client';

const { Text } = Typography;

interface Photo {
  id?: string;
  url?: string;
  caption?: string | null;
  createdAt?: string;
}

interface PhotosResponse {
  reportId?: string | null;
  photos?: Photo[];
}

interface InterventionDetailModalProps {
  interventionId: string | null;
  onClose: () => void;
}

export function InterventionDetailModal({ interventionId, onClose }: InterventionDetailModalProps) {
  const client = useWisecureClient();

  const { data, isLoading } = useWisecureQuery<InterventionDetailResponse>(
    ['intervention-detail', interventionId ?? ''],
    (c) =>
      c.interventions.interventionsControllerFindOne({ id: interventionId! }),
    { enabled: !!interventionId },
  );

  const { data: photosData, isLoading: photosLoading } = useWisecureQuery<PhotosResponse>(
    ['intervention-photos', interventionId ?? ''],
    (c) =>
      c.interventions.interventionsControllerGetPhotos({ id: interventionId! }),
    { enabled: !!interventionId },
  );

  const handleDownloadPdf = async () => {
    if (!interventionId) return;
    try {
      const result = await client.interventions.interventionsControllerGetReportPdfUrl({
        id: interventionId,
      });
      if (result.url) {
        window.open(result.url, '_blank');
      }
    } catch {
      // Error could be shown via notification
    }
  };

  const photos = photosData?.photos ?? [];

  return (
    <Modal
      title="Détail de l'intervention"
      open={!!interventionId}
      onCancel={onClose}
      footer={null}
      width={800}
    >
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin />
        </div>
      ) : data ? (
        <>
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="ID">{data.id}</Descriptions.Item>
            <Descriptions.Item label="Statut">{data.status}</Descriptions.Item>
            <Descriptions.Item label="Adresse">{data.locationAddress ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="Date de début">
              {data.startedAt ? new Date(data.startedAt).toLocaleString('fr-FR') : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Date de fin">
              {data.completedAt ? new Date(data.completedAt).toLocaleString('fr-FR') : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Durée">
              {data.durationSeconds ? `${Math.round(data.durationSeconds / 60)} min` : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Client" span={1}>
              {data.client
                ? `${data.client.clientProfile.firstName ?? ''} ${data.client.clientProfile.lastName ?? ''} (${data.client.email})`
                : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Agent" span={1}>
              {data.agent
                ? `${data.agent.agentProfile.firstName ?? ''} ${data.agent.agentProfile.lastName ?? ''} (${data.agent.email})`
                : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Société" span={2}>
              {data.society?.name ?? '—'}
            </Descriptions.Item>
            {data.report && (
              <Descriptions.Item label="Rapport" span={2}>
                {data.report.notes ?? 'Rapport disponible'}
              </Descriptions.Item>
            )}
          </Descriptions>

          {(photos.length > 0 || data.report) && (
            <>
              <Divider orientation="left">Documents</Divider>
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {data.report && (
                  <Button
                    icon={<DownloadOutlined />}
                    onClick={handleDownloadPdf}
                    block
                  >
                    Télécharger le rapport PDF
                  </Button>
                )}

                {photosLoading ? (
                  <div style={{ textAlign: 'center', padding: 20 }}>
                    <Spin size="small" />
                  </div>
                ) : photos.length > 0 ? (
                  <div>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                      <PictureOutlined /> Photos du rapport ({photos.length})
                    </Text>
                    <Image.PreviewGroup>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {photos.map((photo) => (
                          <Image
                            key={photo.id}
                            src={photo.url}
                            alt={photo.caption ?? 'Photo du rapport'}
                            width={100}
                            height={100}
                            style={{ objectFit: 'cover', borderRadius: 4 }}
                            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mN8/+F/PQAJhAN4f2hM2AAAAABJRU5ErkJggg=="
                          />
                        ))}
                      </div>
                    </Image.PreviewGroup>
                  </div>
                ) : null}
              </Space>
            </>
          )}
        </>
      ) : (
        <Text>Intervention non trouvée</Text>
      )}
    </Modal>
  );
}
